// ProductDetail.jsx
// Full product details page: large image, description, price, and Add-to-Cart.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../utils/pricing";
import { starRating } from "../utils/helpers";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { currentUser } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false); // brief "added!" feedback

  const inCart = cartItems.some((i) => i.id === id);

  // ── Fetch product from Firestore ───────────────────────────────────────────
  useEffect(() => {
    async function fetchProduct() {
      try {
        const docRef = doc(db, "products", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [id, navigate]);

  function handleAddToCart() {
    if (!currentUser) {
      toast.info("Please login to add items to your cart.");
      navigate("/login");
      return;
    }

    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="h-96 bg-gray-200 rounded-2xl" />
          <div className="space-y-4">
            <div className="h-5 bg-gray-200 rounded w-1/4" />
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-10 bg-gray-200 rounded w-1/3 mt-6" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Category colour mapping
  const categoryColors = {
    "Gift Items": "bg-pink-100 text-pink-700",
    Toys: "bg-yellow-100 text-yellow-700",
    Games: "bg-blue-100 text-blue-700",
    Handicrafts: "bg-green-100 text-green-700",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-6 flex items-center gap-2">
        <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
        <span>›</span>
        <Link to={`/?category=${product.category}`} className="hover:text-red-600 transition-colors">
          {product.category}
        </Link>
        <span>›</span>
        <span className="text-gray-900 truncate">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Product image */}
        <div className="rounded-2xl overflow-hidden bg-gray-50 shadow-sm">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-96 object-cover"
            onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=No+Image"; }}
          />
        </div>

        {/* Product info */}
        <div className="flex flex-col justify-between">
          <div>
            {/* Category badge */}
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[product.category] || "bg-gray-100 text-gray-600"}`}>
              {product.category}
            </span>

            {/* Name */}
            <h1 className="text-3xl font-bold text-gray-900 mt-3 mb-2 leading-tight">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-yellow-400 text-lg">{starRating(product.rating || 0)}</span>
              <span className="text-sm text-gray-500">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

            {/* Stock indicator */}
            {product.stock > 0 ? (
              <p className="text-sm text-green-600 font-medium mb-4">
                ✓ In Stock ({product.stock} available)
              </p>
            ) : (
              <p className="text-sm text-red-500 font-medium mb-4">✗ Out of Stock</p>
            )}
          </div>

          {/* Price + CTA */}
          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-end gap-3 mb-5">
              <span className="text-4xl font-extrabold text-red-600">
                {formatCurrency(product.price)}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all ${product.stock === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : added
                    ? "bg-green-600 text-white"
                    : inCart
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-red-600 text-white hover:bg-red-700 active:scale-95"
                  }`}
              >
                {added ? "✓ Added to Cart!" : inCart ? "✓ In Cart" : "Add to Cart"}
              </button>

              <Link
                to="/cart"
                className="flex-1 py-3 rounded-xl font-semibold text-sm text-center border-2 border-red-600 text-red-600 hover:bg-red-50 transition-colors"
              >
                View Cart
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
